import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Layers, Network, LineChart, Lock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Header } from "../components/Header";
import SEO from "../components/SEO";
import { Footer } from "../components/Footer";
import { adminLogin } from "../lib/adminApi";
import { hasConsultantWorkspace, hasSmartEwm, hasApp, APP_SMARTEVALUATE, APP_SMARTBID } from "../lib/applications";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND_CACHE_KEY = "primes4.brand.logo.v1";

// PrimeS4 AI ecosystem positioning cards — replaces the older
// WarehouseX-only recommendation cards. Keep copy enterprise-flat.
const FEATURE_CARDS = [
  {
    icon: Layers,
    title: "PRIVA Operational Intelligence",
    desc: "Access AI-assisted operational intelligence, orchestration, enterprise visibility, and intelligent execution support.",
    tone: "purple",
  },
  {
    icon: Network,
    title: "Platform Ecosystem Access",
    desc: "Access WarehouseX, SmartInspect, SmartRF, SmartCenter, SmartEvaluate, SmartImprove, and future enterprise platforms.",
    tone: "blue",
  },
  {
    icon: LineChart,
    title: "Transformation & Execution Insights",
    desc: "Track recommendations, initiatives, execution progress, operational KPIs, ROI, and business outcomes.",
    tone: "blue",
  },
];

// Login redirect — sends the user to the first workspace they can actually
// access. Admins go to the admin panel; users with only SmartEWM land in the
// standalone SmartEWM shell; everyone else lands in the consultant workspace.
const postLoginRoute = (user) => {
  const role = (user?.role || "").toLowerCase();
  if (role === "admin") return "/admin/questions";

  const consultantAccess = hasConsultantWorkspace(user);
  const ewmAccess = hasSmartEwm(user);

  if (role === "client") {
    // Client behaviour is unchanged — except SmartEWM-only clients (the demo
    // supervisor) still land directly in the SmartEWM shell as before.
    if (ewmAccess && !consultantAccess) return "/app/smartewmbot";
    return "/transformation/dashboard";
  }
  // Consultant / Demo
  if (consultantAccess) {
    // SmartBid-only users (no SmartEvaluate) land on the SmartBid dashboard so
    // they don't hit an empty SmartEvaluate list.
    if (!hasApp(user, APP_SMARTEVALUATE) && hasApp(user, APP_SMARTBID)) return "/consultant/smartbid";
    return "/consultant/assessments";
  }
  if (ewmAccess) return "/app/smartewmbot";
  // No mapped application — fall back to the consultant workspace shell which
  // will render an empty, access-aware state.
  return "/consultant/assessments";
};

// Pull the PrimeS4 AI brand mark (same source the global Header / Footer use)
// so the login screen reflects the ecosystem identity, not the
// WarehouseX-only product mark. Admin updates flow through automatically.
function readBrandCache() {
  try {
    const raw = localStorage.getItem(BRAND_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.logo === "string") return parsed;
  } catch { /* ignore */ }
  return null;
}
function writeBrandCache(logo) {
  try {
    localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify({ logo, fetched_at: Date.now() }));
  } catch { /* ignore */ }
}

function usePrimeS4BrandLogo() {
  const cached = readBrandCache();
  const [logo, setLogo] = useState(cached ? cached.logo : "");
  const [resolved, setResolved] = useState(cached !== null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/branding/public`)
      .then((r) => {
        if (cancelled) return;
        const b = r.data || {};
        // Login screen logo is sourced ONLY from Platform Branding → Platform
        // Logo. Partner logo intentionally not used here; it remains for
        // client-facing surfaces (Client View / PDF reports).
        const next = b.platform_logo || "";
        setLogo(next);
        setResolved(true);
        writeBrandCache(next);
      })
      .catch(() => { if (!cancelled) setResolved(true); });
    return () => { cancelled = true; };
  }, []);

  return { logo, resolved };
}

export default function TransformationPortal() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logo: brandLogo, resolved: brandResolved } = usePrimeS4BrandLogo();

  // Support `/transformation?email=…` pre-fill (existing-user portal invites).
  useEffect(() => {
    const prefill = searchParams.get("email");
    if (prefill) {
      setEmail(prefill);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="portal-login-password"]');
        if (el) el.focus();
      }, 50);
    }
  }, [searchParams]);

  // Returning authenticated users: a signed-in Client Access user who lands on
  // the portal (direct URL / refresh / main-site Login) is routed straight to
  // My Evaluations instead of seeing the login form or the legacy portal.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { adminMe } = await import("../lib/adminApi");
        const me = await adminMe();
        if (cancelled || !me) return;
        if ((me.role || "").toLowerCase() === "client") {
          const { resolveClientLanding } = await import("../lib/clientAccess");
          const landing = await resolveClientLanding();
          if (!cancelled && landing) navigate(landing, { replace: true });
        }
      } catch { /* not signed in — show the login form */ }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    // Password-manager / browser autofill writes the DOM input value but
    // does not always fire React's onChange — the first submit can land
    // with empty state and produce a 401 "Login failed", while a second
    // click (after the focus/blur cycle synchronises state) succeeds. To
    // make the first click reliable we read the live DOM values as a
    // fallback and also resync them into React state.
    let liveEmail = email;
    let livePassword = password;
    try {
      const emailEl = e.currentTarget.querySelector(
        '[data-testid="portal-login-email"]',
      );
      const passEl = e.currentTarget.querySelector(
        '[data-testid="portal-login-password"]',
      );
      if (emailEl && emailEl.value && emailEl.value !== email) {
        liveEmail = emailEl.value;
        setEmail(emailEl.value);
      }
      if (passEl && passEl.value && passEl.value !== password) {
        livePassword = passEl.value;
        setPassword(passEl.value);
      }
    } catch {
      /* DOM access defensive — fall back to React state */
    }
    // Attempt login. Real auth failures (401 "Invalid credentials") surface
    // immediately. Transport-level failures (no HTTP response at all, or a
    // 502/503/504 gateway hiccup on the FIRST request after the SPA loads —
    // the production "first attempt fails, second works" race) are retried
    // ONCE on a warm connection before we show an error.
    const attempt = async () => {
      try {
        return { user: await adminLogin(liveEmail, livePassword) };
      } catch (err) {
        return { err };
      }
    };
    const isTransient = (err) =>
      !err?.response || err.code === "ERR_NETWORK" || err.response.status >= 502;

    let { user, err } = await attempt();
    if (err && isTransient(err)) {
      await new Promise((r) => setTimeout(r, 400));
      ({ user, err } = await attempt());
    }

    if (user) {
      // Client Access users land on My Evaluations; legacy Client Portal users
      // (and SmartEWM-only clients) keep their existing route.
      if ((user?.role || "").toLowerCase() === "client") {
        try {
          const { resolveClientLanding } = await import("../lib/clientAccess");
          const landing = await resolveClientLanding();
          if (landing) {
            navigate(landing);
            setLoading(false);
            return;
          }
        } catch { /* fall through to default client routing */ }
      }
      navigate(postLoginRoute(user));
    } else {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <SEO
        title="PrimeS4 AI Access Portal"
        description="Secure access to PrimeS4 AI platforms and operational intelligence applications."
        canonical="/transformation"
        noindex
      />
      <Header variant="solid" />

      <main className="relative flex-1 py-16 md:py-20">
        {/* Subtle enterprise backdrop — extremely faint grid + two soft tinted
            glows. Keeps the surface white and executive while hinting at the
            connected AI ecosystem visual language used elsewhere. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none overflow-hidden"
        >
          <div
            className="absolute inset-0 opacity-[0.05]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(2,89,121,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(2,89,121,0.7) 1px, transparent 1px)",
              backgroundSize: "56px 56px",
              maskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
              WebkitMaskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
            }}
          />
          <div
            className="absolute -top-40 -left-32 w-[460px] h-[460px] rounded-full blur-3xl opacity-25"
            style={{ background: "radial-gradient(circle, #BAE6FD 0%, transparent 65%)" }}
          />
          <div
            className="absolute top-1/2 -right-40 w-[420px] h-[420px] rounded-full blur-3xl opacity-20"
            style={{ background: "radial-gradient(circle, #DDD6FE 0%, transparent 65%)" }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* LEFT — Ecosystem positioning */}
            <div className="lg:col-span-7">
              <p
                className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#025979] mb-3 flex items-center gap-3"
                data-testid="portal-eyebrow"
              >
                <span className="inline-block h-px w-8 bg-[#025979]" />
                PrimeS4 AI Access
              </p>
              <h1
                className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-[#0F172A] tracking-tight leading-[1.1]"
                style={{ fontFamily: "Chivo, sans-serif" }}
                data-testid="portal-title"
              >
                PrimeS4 AI Access Portal
              </h1>
              <p
                className="mt-5 text-base md:text-lg text-[#4B5563] leading-relaxed max-w-2xl"
                data-testid="portal-subtitle"
              >
                Access PrimeS4 AI platforms, operational intelligence applications, transformation dashboards, and enterprise execution ecosystems.
              </p>

              <div className="mt-10 space-y-4" data-testid="portal-feature-cards">
                {FEATURE_CARDS.map((v) => {
                  const Icon = v.icon;
                  const slug = v.title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  const purple = v.tone === "purple";
                  return (
                    <div
                      key={v.title}
                      data-testid={`portal-feature-${slug}`}
                      className={`bg-white border border-[#E5E7EB] rounded-sm p-5 flex items-start gap-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                        purple ? "hover:border-[#A78BFA]" : "hover:border-[#0093cb]"
                      }`}
                    >
                      <span
                        className="w-11 h-11 rounded-sm flex items-center justify-center shrink-0"
                        style={{
                          background: purple
                            ? "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)"
                            : "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)",
                        }}
                      >
                        <Icon
                          className={`w-5 h-5 ${purple ? "text-[#7C3AED]" : "text-[#025979]"}`}
                          strokeWidth={1.75}
                        />
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3
                          className="text-base font-semibold text-[#0F172A] leading-snug"
                          style={{ fontFamily: "Chivo, sans-serif" }}
                        >
                          {v.title}
                        </h3>
                        <p className="mt-1.5 text-[13px] text-[#4B5563] leading-relaxed">
                          {v.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* RIGHT — Sign-in panel */}
            <div className="lg:col-span-5">
              <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-8 lg:sticky lg:top-24">
                <div
                  className="flex flex-col items-center text-center mb-6"
                  data-testid="login-brand"
                >
                  {brandLogo ? (
                    <img loading="lazy"
                      src={brandLogo}
                      alt="PrimeS4 AI"
                      className="h-10 w-auto object-contain"
                      data-testid="login-brand-logo"
                    />
                  ) : brandResolved ? (
                    <span
                      className="text-xl font-bold text-[#0F172A] tracking-tight"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                      data-testid="login-brand-wordmark"
                    >
                      PrimeS4 AI
                    </span>
                  ) : (
                    <span aria-hidden="true" className="h-10 w-32" />
                  )}
                  <p
                    className="mt-2 text-[11px] text-[#9CA3AF] tracking-wide"
                    data-testid="login-powered-by"
                  >
                    Powered by PRIVA Operational Intelligence
                  </p>
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-[#025979]" />
                  <h2
                    className="text-lg font-bold text-[#111827]"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                    data-testid="login-title"
                  >
                    Sign in to PrimeS4 AI
                  </h2>
                </div>
                <p className="text-xs text-[#6B7280] mb-6" data-testid="login-subtitle">
                  Secure access to PrimeS4 AI platforms and operational intelligence applications.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-[#374151] block mb-1">
                      Email
                    </label>
                    <Input
                      data-testid="portal-login-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#374151] block mb-1">
                      Password
                    </label>
                    <Input
                      data-testid="portal-login-password"
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="rounded-sm"
                    />
                  </div>
                  {error && (
                    <p
                      data-testid="portal-login-error"
                      className="text-xs text-red-500"
                    >
                      {error}
                    </p>
                  )}
                  <Button
                    data-testid="portal-login-btn"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-sm h-10"
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>

                <div className="mt-3 text-center">
                  <button
                    type="button"
                    data-testid="portal-forgot-link"
                    onClick={() => navigate("/admin/forgot-password")}
                    className="text-xs text-[#025979] hover:text-[#03738C] transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>

                <p
                  className="mt-6 text-xs text-center text-[#9CA3AF]"
                  data-testid="login-platform-access"
                >
                  PrimeS4 AI Platform Access
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
